<template>
    <!-- "Add New Unit" of Create Universal Quote (Android UnitFragment / UnitAdapter item_unit from UQConfigFragment
         "add_unit_btn"): name, unit type, tonnage (not Boiler) / heating BTU (Boiler), placement (Package) / air handler
         location (Split), manufacture date, coil model (Package / Split), serial + model per part
         (Split: outdoor + indoor, Package: package unit = outdoor, Boiler: boiler unit = outdoor).
         Saves through units/update for the quote source (address / appointment / RQ / S&R); the answer is the unit list. -->
    <div class="uqc w-full flex flex-col gap-[12px] px-[6px] font-roboto">
        <h4 class="m-0 text-[16px] font-medium text-[#F67280]">
            Add New Unit
        </h4>
        <p class="m-0 text-[14px] text-[#666666]">
            Please enter the unit information to continue.
        </p>

        <div>
            <label class="uqcLabel">Unit Name <span class="text-[#EE5851]">*</span></label>
            <inputComponent ref="nameRef" v-model="unit.unit_name" name="unitName" required placeholder="Enter Unit Name"
                required-message="Unit name is required" :custom-class="fieldClass" />
        </div>
        <div>
            <label class="uqcLabel">Unit Type <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="unit.type" :options="SYSTEM_TYPES" placeholder="Select Unit Type" @change="onTypeChange" />
        </div>
        <div v-if="unit.type && unit.type !== 'Boiler'">
            <label class="uqcLabel">Tonnage <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="unit.tonnage" :options="TONNAGES" placeholder="Select Tonnage" />
        </div>
        <div v-if="unit.type === 'Boiler'">
            <label class="uqcLabel">Heating BTU</label>
            <inputComponent v-model="unit.heating_btu" name="unitBtu" placeholder="Enter BTU" inputmode="numeric"
                :regex="/^\d+$/" error-message="Please enter a valid number" :custom-class="fieldClass" />
        </div>
        <div v-if="unit.type === 'Package'">
            <label class="uqcLabel">Placement <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="unit.package" :options="PLACEMENTS" placeholder="Select Placement" />
        </div>
        <div v-if="unit.type === 'Split'">
            <label class="uqcLabel">Air Handler Location <span class="text-[#EE5851]">*</span></label>
            <FxSelect v-model="unit.split" :options="AIR_HANDLER_LOCATIONS" placeholder="Select Air Handler Location" />
        </div>
        <div>
            <label class="uqcLabel">Manufacture Date <span class="text-[#EE5851]">*</span></label>
            <date-picker v-model:value="unit.manufacture_date" class="uqDate" format="MM/DD/YYYY" value-type="format"
                placeholder="Select Date" :clearable="false" :editable="false" :disabled-date="disabledAfter"
                :append-to-body="false" />
        </div>
        <div v-if="unit.type === 'Package' || unit.type === 'Split'">
            <label class="uqcLabel">Coil Model</label>
            <inputComponent v-model="unit.gas_furnace.model" name="unitCoil" placeholder="Enter coil model"
                :custom-class="fieldClass" />
        </div>
        <!-- serial / model per part (Android SystemAgreementChildAdapter); optional like on Android -->
        <div v-for="part in parts" :key="part.key" class="flex flex-col gap-[10px] pt-[4px]">
            <p class="m-0 text-[15px] font-medium text-[#333333]">{{ part.label }}</p>
            <div>
                <label class="uqcLabel">Serial Number</label>
                <inputComponent v-model="unit[part.key].serial" :name="'serial-' + part.key" placeholder="Enter serial number"
                    :custom-class="fieldClass" />
            </div>
            <div>
                <label class="uqcLabel">Model Number</label>
                <inputComponent v-model="unit[part.key].model" :name="'model-' + part.key" placeholder="Enter model number"
                    :custom-class="fieldClass" />
            </div>
        </div>
    </div>

    <div class="uqcFooter mt-auto flex items-center justify-end gap-[10px] px-[6px]">
        <button type="button" class="uqcBtnCancel" :disabled="saving" @click="$emit('cancel')">
            Cancel
        </button>
        <button type="button" class="uqcBtnNext" :disabled="saving" @click="save">
            Add Unit
        </button>
    </div>
</template>

<script>
import DatePicker from "vue-datepicker-next";
import "vue-datepicker-next/index.css";
import { useToast } from "vue-toastification";
import inputComponent from "../../partialComponent/inputComponent.vue";
import FxSelect from "../../partialComponent/FxSelect.vue";
import { uqApi } from "../../../services/uqApi";

const toast = useToast();

const opts = list => list.map(v => ({ label: String(v), value: v }));

export default {
    components: { DatePicker, inputComponent, FxSelect },
    props: {
        // where the unit belongs: { address_id } or { source_type, source_id } (same params as units/list)
        target: { type: Object, required: true },
    },
    emits: ["cancel", "saved"],
    data() {
        return {
            // same lists as the Android UnitAdapter (tonnage sizes = boot config `sizes`)
            SYSTEM_TYPES: opts(["Package", "Split", "Boiler"]),
            TONNAGES: opts([0.5, 0.75, 1, 1.5, 2, 2.5, 3, 3.5, 4, 5]),
            PLACEMENTS: opts(["Ground", "Roof"]),
            AIR_HANDLER_LOCATIONS: opts(["Attic", "Crawl Space", "Basement", "Garage", "Closet"]),
            unit: {
                unit_name: "", type: null, tonnage: null, package: null, split: null, manufacture_date: null, heating_btu: "",
                outdoor: { unit: "", serial: "", model: "" },
                indoor: { unit: "Indoor Unit", serial: "", model: "" },
                gas_furnace: { model: "" },
            },
            saving: false,
            fieldClass: "!h-[40px] !px-[12px] !rounded-[8px] !border-[#E1E5EF] !text-[14px] !text-[#333333]",
        };
    },
    computed: {
        // Android UnitAdapter: Split → Outdoor + Indoor, Package → Package Unit, Boiler → Boiler Unit (both in `outdoor`)
        parts() {
            const t = this.unit.type;
            if (t === "Split") return [{ key: "outdoor", label: "Outdoor Unit" }, { key: "indoor", label: "Indoor Unit" }];
            if (t === "Package") return [{ key: "outdoor", label: "Package Unit" }];
            if (t === "Boiler") return [{ key: "outdoor", label: "Boiler Unit" }];
            return [];
        },
    },
    methods: {
        onTypeChange() {
            this.unit.package = null;
            this.unit.split = null;
            if (this.unit.type === "Boiler") this.unit.tonnage = null;
            else this.unit.heating_btu = "";
        },
        disabledAfter(date) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date > today;
        },
        // Android UnitFragment.checkInputValidation
        validate() {
            const u = this.unit;
            const msg = !u.unit_name?.trim() ? "Unit name is required."
                : !u.type ? "Please select System Type."
                    : (u.type !== "Boiler" && !(Number(u.tonnage) > 0)) ? "Please select Tonnage."
                        : (u.type === "Package" && !u.package) ? "Please select Placement."
                            : (u.type === "Split" && !u.split) ? "Please select Air Handler Location."
                                : !u.manufacture_date ? "Manufacture date is required."
                                    : null;
            if (msg) toast.error(msg);
            return !msg;
        },
        async save() {
            if (this.saving || !this.validate()) return;
            this.saving = true;
            const u = this.unit;
            const unit = { ...u, unit_name: u.unit_name.trim(), outdoor: { ...u.outdoor, unit: this.parts[0]?.label || "" } };
            // only Split has an indoor part; the coil model is not asked for a Boiler
            if (u.type !== "Split") delete unit.indoor;
            if (u.type === "Boiler") delete unit.gas_furnace;
            const res = await uqApi.saveUnits(this.target, [unit]);
            this.saving = false;
            if (!res.ok || !Array.isArray(res.body)) {
                toast.error((typeof res.body === "string" && res.body) || res.message || "Unable to add the unit.");
                return;
            }
            toast.success("Unit Added Successfully.");
            this.$emit("saved", res.body);
        },
    },
};
</script>

<style scoped>
/* the date field looks like the other 40px inputs and opens its calendar right under it (inside the modal) */
.uqDate {
    position: relative;
    width: 100%;
}
.uqDate :deep(.mx-input) {
    height: 40px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid #E1E5EF;
    font-size: 14px;
    color: #333333;
    box-shadow: none;
}
.uqDate :deep(.mx-datepicker-popup) {
    top: 100% !important;
    left: 0 !important;
    bottom: auto !important;
    margin-top: 4px;
    z-index: 20;
}
</style>
